import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getLocalizedText } from "../../../util/localizedText";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { setIsMapOpen } from "../../../redux/slice/apartmentRental/apartmentRentalSlice";
import { useEnsureApartmentDetails } from "../common/hooks/useEnsureApartmentDetails";
import { roomGalleryImages } from "../common/util/galleryImages";
import FullLockerView from "../common/components/locationFromBottomBar/FullLockerView";
import ImageGallery, { ImageGalleryImage } from "../../../components/ImageGallery";
import ObjectDetailsFailure from "../common/components/ObjectDetailsFailure";
import LoaderOverlay from "../../layout/components/LoaderOverlay";
import { useTheme } from "@mui/material";
import { formatCurrency } from "../../../util";
import PriceTagMap, {
    PriceTagMarker,
} from "../common/components/ApartmentRental/PriceTagMap";
import { OwnRoomPin, roomIsPinned, withOwnRoom } from "../common/components/ApartmentRental/ownRoomMarker";
import FullScreenDialog from "../../common/FullScreenDialog";
import {
    Accordion,
    AccordionItem,
    AccordionTrigger,
    AccordionContent,
} from "../../../components/ui/accordion";
import MapView from "../common/components/ApartmentRental/MapView";
import { mapEmbedSrc } from "../../../util/mapEmbed";

const ApartmentOverview = () => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const [openImage, setOpenImage] = useState(false);
    const [selectedImage, setSelectedImage] = useState("");

    const { selectedApartment, locationApartmentList, isMapOpen } = useSelector(
        (state: RootState) => state.apartment
    );
    const tenantConfig = useSelector((state: RootState) => state.tenantConfig.config);

    const { data, isLoading, isError } = useEnsureApartmentDetails();

    // Bullets render every amenity except `type === "Text"` (handled as a paragraph block below).
    // The previous `type === "General"` filter silently dropped custom Admin-UI entries.
    const generalAmenities = useMemo(() => {
        return (
            data?.accomodationdetail?.amenities?.filter((amenity) => amenity.type !== "Text") || []
        );
    }, [data]);

    const otherAmenities = useMemo(() => {
        return (
            data?.accomodationdetail?.amenities?.filter((amenity) => amenity.type === "Text") || []
        );
    }, [data]);

    const handleOpenImage = (image: string) => {
        setSelectedImage(image);
        setOpenImage(true);
    };

    const handleOpenMap = useCallback(() => {
        if (!data) return;
        dispatch(setIsMapOpen(true));
    }, [data, dispatch]);

    // The room itself, from its own address: its map must show it even when
    // there are no search results to pin (a direct link, an empty search).
    const ownRoom = useMemo<OwnRoomPin | null>(() => (data
        ? {
            id: data.id,
            label: data.name,
            lat: data.Group?.location?.address?.geoLat,
            lng: data.Group?.location?.address?.geoLng,
        }
        : null), [data]);

    const resultMarkers = useMemo<PriceTagMarker[]>(() => {
        const selectedId = selectedApartment?.assetId || selectedApartment?.id || null;
        const source =
            Array.isArray(locationApartmentList) && locationApartmentList.length > 0
                ? locationApartmentList
                : selectedApartment
                ? [selectedApartment]
                : [];

        return source
            .filter(
                (apt) =>
                    typeof apt?.geoLat === "number" &&
                    typeof apt?.geoLng === "number" &&
                    !Number.isNaN(apt.geoLat) &&
                    !Number.isNaN(apt.geoLng)
            )
            .map((apt) => {
                const priceValue = Number(apt?.price ?? 0);
                const hasPrice = !Number.isNaN(priceValue) && priceValue > 0;
                const symbol = tenantConfig?.currencySymbol ?? "";
                const formattedNumeric = priceValue.toLocaleString(i18n.language, {
                    minimumFractionDigits: priceValue % 1 === 0 ? 0 : 2,
                    maximumFractionDigits: 2,
                });

                const label = hasPrice
                    ? symbol
                        ? `${symbol}${formattedNumeric}`
                        : formatCurrency(priceValue, i18n.language)
                    : t("apartment.price_on_request");

                return {
                    id: apt.assetId || apt.id,
                    lat: apt.geoLat,
                    lng: apt.geoLng,
                    label,
                    price: hasPrice ? priceValue : null,
                    isSelected: selectedId ? selectedId === (apt.assetId || apt.id) : false,
                };
            });
    }, [i18n.language, locationApartmentList, selectedApartment, t, tenantConfig?.currencySymbol]);
    const markers = useMemo(() => withOwnRoom(resultMarkers, ownRoom), [resultMarkers, ownRoom]);

    // The embedded map of the address when Google's script is absent OR this
    // room has no pin: the interactive map would be grey, or centred on the
    // neighbours (ownRoomMarker.ts › roomIsPinned).
    const shouldShowStaticMap =
        typeof window === "undefined" || typeof (window as any).google === "undefined"
        || !roomIsPinned(markers, ownRoom);
    // The selected search result's pin first, as before; then the room's own address.
    const roomAddress = data?.Group?.location?.address;
    const staticMapSrc = mapEmbedSrc(roomAddress, {
        lat: selectedApartment?.geoLat ?? roomAddress?.geoLat,
        lng: selectedApartment?.geoLng ?? roomAddress?.geoLng,
    });

    useEffect(() => {
        // Prevent scrolling on mount
        document.body.style.overflow = "hidden";

        // Cleanup function to restore scrolling when component unmounts
        return () => {
            document.body.style.overflow = "unset";
        };
    }, []);

    const lang = i18n.language as "en" | "de";

    // Resolve amenity labels from the controlled vocabulary (issue #953) via i18n
    // so a customer always sees them in their own language, switching instantly.
    // Pre-existing freeform amenities (key not in the vocabulary, so no
    // translation exists) fall back to their stored langValues/value.
    const resolveAmenityLabel = (amenity: {
        key: string;
        value: string | number;
        langValues?: { en: string; de: string };
    }): string => {
        const translated = t(`amenities.${amenity.key}`, { defaultValue: "" });
        if (translated) return translated;
        return amenity.langValues?.[lang] || String(amenity.value ?? "");
    };

    if (isLoading) {
        return <LoaderOverlay open={isLoading} />;
    }

    // A failed load is not an object without photos or description (#2764).
    if (isError) {
        return <ObjectDetailsFailure />;
    }

    return (
        // Removed h-screen and overflow-hidden to allow parent FullScreenDialog to control scrolling
        <div className="max-w-full mx-auto flex flex-col p-4">
            <ImageGallery
                images={
                    roomGalleryImages(data).map((src) => ({
                        src,
                    })) as ImageGalleryImage[]
                }
                aspectRatio="16/9"
                enableLoop
                className="border border-gray-200"
                onImageClick={(img) => handleOpenImage(img.src)}
            />

            {/* Content now relies on parent container for scrolling (single scrollbar) */}
            <div className="flex-1 mt-4 text-black">
                <h1 className="text-xl font-semibold mb-4">{data?.name}</h1>

                <div className="flex justify-between items-center mb-6">
                    <div className="flex gap-2 text-sm text-gray-600">
                        <span>
                            {data?.accomodationdetail?.guests} {t("apartment.guests")}
                        </span>
                        <span>
                            • {data?.accomodationdetail?.rooms}{" "}
                            {t("apartment.room", {
                                count: data?.accomodationdetail?.rooms ?? 1,
                            })}
                        </span>
                        <span>
                            • {data?.accomodationdetail?.beds}{" "}
                            {t("apartment.bed", {
                                count: data?.accomodationdetail?.beds ?? 1,
                            })}
                        </span>
                        <span>
                            • {data?.accomodationdetail?.baths}{" "}
                            {t("apartment.bath", {
                                count: data?.accomodationdetail?.baths ?? 1,
                            })}
                        </span>
                    </div>
                    <div className="text-right">
                        <span className="mr-1 font-medium">{t("general.from")}</span>
                        <span style={{ color: theme.palette.input.focus }} className="font-medium">
                            {formatCurrency(
                                data?.Group?.pricecategory?.pricescale?.[0]?.price ?? 0,
                                i18n.language
                            )}
                        </span>
                        <span className="text-gray-600 font-medium">/{t("apartment.night")}</span>
                    </div>
                </div>

                <h2 className="text-lg font-semibold mb-2">
                    {t("apartment.facility_description_title")}
                </h2>
                <div className="text-sm text-gray-600 mb-6">
                    {/* #2664's crash class, which survived here: optional-
                        chaining down to `accomodationdetail` and then INDEXING
                        throws the moment the key is absent, and these blobs are
                        optional on every hand-created apartment. Same treatment
                        as the venue sibling — one shared resolver, absent reads
                        as "". The singular `facilityDescription` stays as the
                        fallback it always was. */}
                    {getLocalizedText(data?.accomodationdetail?.facilityDescriptions)
                        || data?.accomodationdetail?.facilityDescription}

                    {(generalAmenities?.length ?? 0) > 0 && (
                        <ul className="list-disc pl-5 mt-3">
                            {generalAmenities.map((amenity, index) => (
                                <li key={index}>
                                    {resolveAmenityLabel(amenity)}
                                </li>
                            ))}
                        </ul>
                    )}

                    {(otherAmenities?.length ?? 0) > 0 && (
                        <div className="mt-3 space-y-3">
                            {otherAmenities.map((amenity, index) => (
                                <p key={index}>
                                    {resolveAmenityLabel(amenity)}
                                </p>
                            ))}
                        </div>
                    )}
                </div>

                {(() => {
                    // Was `importantNotes[lang]` — the same unguarded index.
                    const note = getLocalizedText(data?.accomodationdetail?.importantNotes);
                    return note.length > 0 ? (
                        <>
                            <h2 className="text-lg font-semibold mb-2">
                                {t("apartment.important_note")}
                            </h2>
                            <p className="text-sm text-gray-600 mb-6">{note}</p>
                        </>
                    ) : null;
                })()}

                {(() => {
                    const visible = (data?.accomodationdetail?.policies ?? [])
                        .map((policy) => ({
                            title: policy.titles?.[lang] || policy.title,
                            description: policy.descriptions?.[lang] || policy.description,
                        }))
                        .filter((policy) => policy.title?.length || policy.description?.length);
                    return visible.length > 0 ? (
                        <>
                            <h2 className="text-lg font-semibold mb-2">
                                {t("apartment.policies_title")}
                            </h2>
                            <Accordion type="single" collapsible className="mb-6">
                                {visible.map((policy, index) => (
                                    <AccordionItem key={index} value={`policy-${index}`}>
                                        <AccordionTrigger className="text-sm text-black text-left">
                                            {policy.title || t("apartment.policies_title")}
                                        </AccordionTrigger>
                                        <AccordionContent className="text-sm text-gray-600 whitespace-pre-line">
                                            {policy.description}
                                        </AccordionContent>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </>
                    ) : null;
                })()}

                <h2 className="text-lg font-semibold mb-2">{t("apartment.location")}</h2>
                <div className="w-full h-48 rounded-lg overflow-hidden border border-gray-200 relative group">
                    {shouldShowStaticMap ? (
                        staticMapSrc && (
                            <iframe
                                title="Property Location"
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                src={staticMapSrc}
                                allowFullScreen
                            />
                        )
                    ) : (
                        <PriceTagMap
                            markers={markers}
                            className="absolute inset-0"
                            mapOptions={{
                                gestureHandling: "none",
                                zoom: markers.length === 1 ? 15 : 13,
                            }}
                            fitBoundsPadding={{ top: 32, right: 32, bottom: 32, left: 32 }}
                            zoomOnSingleMarker={15}
                        />
                    )}
                    <button
                        type="button"
                        onClick={handleOpenMap}
                        className="absolute inset-0 flex items-end justify-between bg-gradient-to-t from-black/70 via-black/10 to-transparent p-4 text-white transition-opacity duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary group-hover:opacity-100 cursor-pointer"
                        aria-label={t("apartment.map_view") ?? "Map view"}
                    >
                        <span className="text-left text-sm font-medium">
                            {t("book_location.venue_reservation_overview.view_map_fullscreen", {
                                defaultValue: t("apartment.map_view"),
                            })}
                        </span>
                        <span className="ml-3 text-lg" aria-hidden="true">
                            ↗
                        </span>
                    </button>
                </div>
            </div>

            {/* Full Image View Modal */}
            {selectedImage && openImage && (
                <FullLockerView
                    setOpenImage={setOpenImage}
                    image={selectedImage}
                    openImage={openImage}
                />
            )}

            <FullScreenDialog
                open={isMapOpen}
                className="max-w-full bg-transparent"
                onClose={() => dispatch(setIsMapOpen(false))}
                ariaLabel={t("common.dialog.map")}
            >
                <MapView ownRoom={ownRoom} fallbackAddress={roomAddress} />
            </FullScreenDialog>
        </div>
    );
};

export default ApartmentOverview;
